import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import Box from "@amzn/meridian/box";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Input from "@amzn/meridian/input";
import Toggle from "@amzn/meridian/toggle";
import Tag from "@amzn/meridian/tag";
import Icon from "@amzn/meridian/icon";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Modal, { ModalFooter } from "@amzn/meridian/modal";
import Button from "@amzn/meridian/button";
import DataTable, { DataTableHead, DataTableBody, DataTableRow, DataTableCell, DataTableHeader, DataTableDragButton } from "@amzn/meridian/data-table";
// TODO: MOVE SVG IMAGES TO CDN
import inboundTrailersImage from "../../assets/images/inboundTrailers.svg";
import SearchField from "@amzn/meridian/search-field";
import Select, { SelectOption } from "@amzn/meridian/select";

import { TrailerData } from "./model";
import {
  fetchTrailers,
  clearOverrides,
  verifyTrailers,
  updateTrailerPriorities,
} from "./api";
import Footer from "../../components/Footer";
import "./index.scss";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../utils/constants";
import { useAppDispatch, useAppSelector } from "../../store";
import { showNotification } from "../../store/slices/errorSlice";
import { setTrailerOverrides, TrailerOverrideData } from "../../store/slices/volumesAndWipSlice";

/**
 * Expandable detail row for a trailer
 */
const TrailerExpandedDetail: React.FC<{
  trailer: TrailerData;
  totalTrailers: number;
  onUpdateTrailer: (trailerId: string, updates: Partial<TrailerData>) => void;
  onSetPriority: (trailerId: string, newPriority: number) => void;
}> = React.memo(({ trailer, totalTrailers, onUpdateTrailer, onSetPriority }) => {
  // Reorder reason state (separate from volume override)
  const [reorderReason, setReorderReason] = useState<string>(trailer.reorderReason || "");
  const [reorderComment, setReorderComment] = useState<string>(trailer.reorderComment || "");
  // Volume override reason state (separate from reorder)
  const [volOverrideReason, setVolOverrideReason] = useState<string>(trailer.volumeOverrideReason || "");
  const [volOverrideComment, setVolOverrideComment] = useState<string>(trailer.volumeOverrideComment || "");
  const [volumeOverride, setVolumeOverride] = useState<string>(
    trailer.volume && trailer.volume !== 0 ? trailer.volume.toString() : ""
  );
  // Sync expanded detail volume override when parent trailer.volume changes (from main cell)
  useEffect(() => {
    const parentStr = trailer.volume && trailer.volume !== 0 ? trailer.volume.toString() : '';
    setVolumeOverride(prev => {
      if (parseInt(prev || '0') !== trailer.volume) return parentStr;
      return prev;
    });
  }, [trailer.volume]);

  const reorderReasonsList = ["Red Tag", "Mix", "AZNGs are good, UPS can be unsafe", "Other"];
  const volumeOverrideReasonsList = ["Sealed trailer", "Estimate from YMS", "Carrier manifest", "Other"];

  return (
    <>
      <DataTableRow>
        <DataTableCell colSpan={7}>
          <Row spacing="500" wrap="down">
            <Box>
              <Text type="b300">Original Priority</Text>
              <Heading level={3} type="d50">
                {trailer.originalPriority || trailer.priority}
              </Heading>
            </Box>
            <Box>
              <Text type="b300">Volume Small</Text>
              <Heading level={3} type="d50">
                {trailer.volumeDetails?.small != null ? trailer.volumeDetails.small.toLocaleString() : 'N/A'}
              </Heading>
            </Box>
            <Box>
              <Text type="b300">Volume Medium</Text>
              <Heading level={3} type="d50">
                {trailer.volumeDetails?.medium != null ? trailer.volumeDetails.medium.toLocaleString() : 'N/A'}
              </Heading>
            </Box>
            <Box>
              <Text type="b300">Volume Large</Text>
              <Heading level={3} type="d50">
                {trailer.volumeDetails?.large != null ? trailer.volumeDetails.large.toLocaleString() : 'N/A'}
              </Heading>
            </Box>
            <Box>
              <Text type="b300">Rugs</Text>
              <Heading level={3} type="d50">
                {trailer.volumeDetails?.rugs}
              </Heading>
            </Box>
            <Box>
              <Text type="b300">TVs</Text>
              <Heading level={3} type="d50">
                {trailer.volumeDetails?.tvs}
              </Heading>
            </Box>
            {trailer?.ymsNotes && (
              <Box>
                <Text type="b300">YMS notes</Text>
                <Text type="b300">{trailer.ymsNotes}</Text>
              </Box>
            )}
          </Row>
        </DataTableCell>
      </DataTableRow>

      {/* Volume override row — for trailers needing volume entry */}
      {trailer.volumeOverrideAllowed && (
        <DataTableRow>
          <DataTableCell colSpan={7}>
            <Row spacing="500" alignmentVertical="bottom">
              <Column spacing="100">
                <Text type="b200" color="secondary">Volume Override</Text>
                <Row spacing="100" alignmentVertical="center">
                  <Input
                    width={120}
                    type="number"
                    value={volumeOverride}
                    onChange={(val: string) => {
                      setVolumeOverride(val);
                      onUpdateTrailer(trailer.trailerId, { volume: val ? parseInt(val) : 0 });
                    }}
                    placeholder="Enter volume"
                    size="small"
                    data-testid="volume-override-input"
                  />
                  <Text type="b200" color="secondary">Units</Text>
                </Row>
              </Column>
              <Column spacing="100">
                <Text type="b200" color="secondary">Volume Override Reason</Text>
                <Select
                  value={volOverrideReason}
                  onChange={(val: string) => {
                    setVolOverrideReason(val);
                    onUpdateTrailer(trailer.trailerId, { volumeOverrideReason: val });
                  }}
                  width={200}
                  size="small"
                >
                  {volumeOverrideReasonsList.map((reasonOption) => (
                    <SelectOption
                      key={reasonOption}
                      value={reasonOption}
                      label={reasonOption}
                    />
                  ))}
                </Select>
              </Column>
              <Column spacing="100">
                <Text type="b200" color="secondary">Comment</Text>
                <Input
                  width={200}
                  value={volOverrideComment}
                  onChange={(val: string) => {
                    setVolOverrideComment(val);
                    onUpdateTrailer(trailer.trailerId, { volumeOverrideComment: val });
                  }}
                  placeholder="Optional comment"
                  size="small"
                />
              </Column>
            </Row>
          </DataTableCell>
        </DataTableRow>
      )}

      {/* Direct priority input — useful for large fleets where drag is impractical */}
      {!trailer.volumeOverrideAllowed && (
        <DataTableRow>
          <DataTableCell colSpan={7}>
            <Row spacing="300" alignmentVertical="center">
              <Text type="b200" color="secondary">Set Priority (1–{totalTrailers}):</Text>
              <Input
                type="number"
                width="80px"
                size="small"
                value={String(trailer.priority)}
                onChange={(val: string) => {
                  const num = parseInt(val);
                  if (num >= 1 && num <= totalTrailers) {
                    onSetPriority(trailer.trailerId, num);
                  }
                }}
                placeholder="Priority"
              />
              <Text type="b200" color="secondary">Current: {trailer.priority} of {totalTrailers}</Text>
            </Row>
          </DataTableCell>
        </DataTableRow>
      )}

      {/* Reorder reason/comment row — shows when trailer was reordered */}
      {trailer.originalPriority && trailer.originalPriority !== trailer.priority && (
        <DataTableRow>
          <DataTableCell colSpan={7}>
            <Row spacing="500" alignmentVertical="bottom">
              <Column spacing="100">
                <Text type="b200" color="secondary">Reorder Reason</Text>
                <Select
                  value={reorderReason}
                  onChange={(val: string) => {
                    setReorderReason(val);
                    onUpdateTrailer(trailer.trailerId, { reorderReason: val });
                  }}
                  width={200}
                  size="small"
                >
                  {reorderReasonsList.map((reasonOption) => (
                    <SelectOption
                      key={reasonOption}
                      value={reasonOption}
                      label={reasonOption}
                    />
                  ))}
                </Select>
              </Column>
              <Column spacing="100">
                <Text type="b200" color="secondary">Comment</Text>
                <Input
                  width={200}
                  value={reorderComment}
                  onChange={(val: string) => {
                    setReorderComment(val);
                    onUpdateTrailer(trailer.trailerId, { reorderComment: val });
                  }}
                  placeholder="Optional comment"
                  size="small"
                />
              </Column>
            </Row>
          </DataTableCell>
        </DataTableRow>
      )}
    </>
  );
});

TrailerExpandedDetail.displayName = 'TrailerExpandedDetail';

/**
 * Volume input for main table cell — uses local string state to allow multi-digit entry
 */
const VolumeInput: React.FC<{
  trailerId: string;
  volume: number;
  onUpdate: (trailerId: string, updates: Partial<TrailerData>) => void;
}> = React.memo(({ trailerId, volume, onUpdate }) => {
  const [localValue, setLocalValue] = useState<string>(volume && volume !== 0 ? volume.toString() : '');

  // Sync local value when parent trailer.volume changes (e.g. from expanded detail override)
  useEffect(() => {
    const parentStr = volume && volume !== 0 ? volume.toString() : '';
    setLocalValue(prev => {
      // Only update if the numeric value actually differs (avoid cursor issues)
      if (parseInt(prev || '0') !== volume) return parentStr;
      return prev;
    });
  }, [volume]);

  return (
    <Input
      value={localValue}
      placeholder="Enter volume"
      onChange={(val: string) => setLocalValue(val)}
      onBlur={() => onUpdate(trailerId, { volume: localValue ? parseInt(localValue) : 0 })}
      type="number"
      width="100px"
      size="small"
    />
  );
});

VolumeInput.displayName = 'VolumeInput';

/**
 * Inbound Trailer Volume component
 */
interface InboundTrailerVolumeProps {
  readOnly?: boolean;
}

const InboundTrailerVolume: React.FC<InboundTrailerVolumeProps> = ({ readOnly = true }) => {
  const [trailers, setTrailers] = useState<TrailerData[]>([]);
  const [originalTrailers, setOriginalTrailers] = useState<TrailerData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandAll, setExpandAll] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState("");

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Read previously saved trailer overrides from Redux
  const savedTrailerOverrides = useAppSelector((state) => state.volumesAndWip.trailerOverrides);

  // Fetch trailers on component mount and merge saved overrides
  useEffect(() => {
    const loadTrailers = async () => {
      try {
        setLoading(true);
        let data = await fetchTrailers();

        // Merge saved overrides from Redux (from previous visit to this page)
        if (savedTrailerOverrides && savedTrailerOverrides.length > 0) {
          data = data.map(trailer => {
            const saved = savedTrailerOverrides.find(o => o.trailerId === trailer.trailerId);
            if (saved) {
              return {
                ...trailer,
                priority: saved.priority,
                originalPriority: saved.originalPriority,
                volume: saved.volume,
                reorderReason: saved.reorderReason,
                reorderComment: saved.reorderComment,
                volumeOverrideReason: saved.volumeOverrideReason,
                volumeOverrideComment: saved.volumeOverrideComment,
              };
            }
            return trailer;
          });
        } else {
          // If no saved overrides in Redux, check if backend returned override data
          // (e.g., after page refresh with an existing planId that has saved overrides)
          const backendOverrides: TrailerOverrideData[] = data
            .filter((t: any) => t.priorityOverride !== undefined || t.volumeOverride !== undefined)
            .map((t: any) => ({
              trailerId: t.trailerId,
              priority: t.priorityOverride ?? t.priority,
              originalPriority: t.priority,
              volume: t.volumeOverride ?? t.volume,
              reorderReason: t.reorderReason,
              reorderComment: t.reorderComment,
              volumeOverrideReason: t.volumeOverrideReason,
              volumeOverrideComment: t.volumeOverrideComment,
            }));
          if (backendOverrides.length > 0) {
            dispatch(setTrailerOverrides(backendOverrides));
            // Also apply to local state
            data = data.map(trailer => {
              const bo = backendOverrides.find(o => o.trailerId === trailer.trailerId);
              if (bo) {
                return { ...trailer, priority: bo.priority, originalPriority: bo.originalPriority, volume: bo.volume };
              }
              return trailer;
            });
          }
        }
        
        setTrailers(data);
        setOriginalTrailers(data);
        // Do NOT auto-expand zero-volume trailers — with large fleets (2000+) this causes
        // excessive UI clutter. Users can expand individual trailers manually to enter volume.
      } catch (error) {
        console.error("Error fetching trailers:", error);
      } finally {
        setLoading(false);
      }
    };
    loadTrailers();
  }, []);

  // Handle expand all toggle — only react to expandAll changes, not trailer changes
  useEffect(() => {
    if (expandAll) {
      setExpandedRows(new Set(trailers.map(t => t.trailerId)));
    } else {
      setExpandedRows(new Set());
    }
  }, [expandAll]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle search
  const handleSearch = (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim() === "") {
      setTrailers(originalTrailers);
    } else {
      const filteredData = originalTrailers.filter(
        (trailer) =>
          trailer?.trailerId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          trailer?.carrier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          trailer?.location?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setTrailers(filteredData);
    }
  };

  // Toggle individual row expansion
  const toggleRow = (trailerId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(trailerId)) {
        next.delete(trailerId);
      } else {
        next.add(trailerId);
      }
      return next;
    });
  };

  // Use ref to avoid recreating handleReorder on every trailers change
  const trailersRef = useRef(trailers);
  trailersRef.current = trailers;

  // Handle reorder via DataTable's native drag — stable callback (no trailers dependency)
  const handleReorder = useCallback(
    (originIndex: number | undefined, destinationIndex: number | undefined, _rowId: string | undefined): boolean => {
      if (originIndex === undefined || destinationIndex === undefined || originIndex === destinationIndex) {
        return true;
      }

      const currentTrailers = trailersRef.current;
      const reordered = [...currentTrailers];
      const [moved] = reordered.splice(originIndex, 1);
      reordered.splice(destinationIndex, 0, moved);

      // Only update priority for affected range instead of all trailers
      const minIdx = Math.min(originIndex, destinationIndex);
      const maxIdx = Math.max(originIndex, destinationIndex);
      const updated = reordered.map((trailer, idx) => {
        if (idx >= minIdx && idx <= maxIdx) {
          return {
            ...trailer,
            originalPriority: trailer.originalPriority || trailer.priority,
            priority: idx + 1,
          };
        }
        // Outside affected range — only update priority number if it changed
        return trailer.priority !== idx + 1
          ? { ...trailer, priority: idx + 1 }
          : trailer;
      });

      setTrailers(updated);

      // Auto-expand only the moved row (not all reordered rows to avoid mass expansion)
      const movedTrailer = updated[destinationIndex];
      if (movedTrailer && movedTrailer.originalPriority && movedTrailer.originalPriority !== movedTrailer.priority) {
        setExpandedRows(prev => {
          const next = new Set(prev);
          next.add(movedTrailer.trailerId);
          return next;
        });
      }

      // Save to backend asynchronously — preserve expanded state
      updateTrailerPriorities(updated).then((savedTrailers) => {
        setOriginalTrailers(savedTrailers);
      }).catch((error) => {
        console.error("Error updating trailer priorities:", error);
      });

      return true;
    },
    [] // Stable — reads from ref
  );

  // Handle clear overrides
  const handleClearOverrides = async () => {
    try {
      await clearOverrides();
      const data = await fetchTrailers();
      setTrailers(data);
      setOriginalTrailers(data);
    } catch (error) {
      console.error("Error clearing overrides:", error);
    }
  };

  // Handle updating trailer fields (reason, comment, volume override)
  const handleUpdateTrailer = useCallback((trailerId: string, updates: Partial<TrailerData>) => {
    setTrailers(prev => prev.map(t =>
      t.trailerId === trailerId ? { ...t, ...updates } : t
    ));
  }, []);

  // Handle direct priority input — moves trailer to target position, shifts others
  const handleSetPriority = useCallback((trailerId: string, newPriority: number) => {
    setTrailers(prev => {
      const currentIdx = prev.findIndex(t => t.trailerId === trailerId);
      if (currentIdx === -1) return prev;
      const destIdx = Math.min(Math.max(newPriority - 1, 0), prev.length - 1);
      if (currentIdx === destIdx) return prev;

      const reordered = [...prev];
      const [moved] = reordered.splice(currentIdx, 1);
      reordered.splice(destIdx, 0, moved);

      return reordered.map((t, idx) => ({
        ...t,
        originalPriority: t.trailerId === trailerId ? (t.originalPriority || t.priority) : t.originalPriority,
        priority: idx + 1,
      }));
    });
  }, []);

  // Handle verify — block if reordered trailers lack reasons
  const handleVerify = async () => {
    // Check for reordered trailers missing reasons
    const missingReasons = trailers.filter(
      t => t.originalPriority && t.originalPriority !== t.priority && !t.reorderReason
    );
    if (missingReasons.length > 0) {
      // Auto-expand rows missing reasons
      setExpandedRows(prev => {
        const next = new Set(prev);
        missingReasons.forEach(t => next.add(t.trailerId));
        return next;
      });
      dispatch(showNotification({
        title: "Reorder Reason Required",
        message: `Please provide a reason for reordering: ${missingReasons.map(t => t.trailerId).join(', ')}`,
        type: "warning"
      }));
      return;
    }

    // Log all overrides for debugging
    const overrides = trailers
      .filter(t => t.reorderReason || t.volumeOverrideReason || (t.originalPriority && t.originalPriority !== t.priority) || t.volumeOverrideAllowed)
      .map(t => ({
        trailerId: t.trailerId,
        priority: t.priority,
        originalPriority: t.originalPriority,
        volume: t.volume,
        reorderReason: t.reorderReason,
        reorderComment: t.reorderComment,
        volumeOverrideReason: t.volumeOverrideReason,
        volumeOverrideComment: t.volumeOverrideComment,
      }));
    console.log('[Inbound Trailers] Overrides on verify:', JSON.stringify(overrides, null, 2));

    // Save trailer overrides to Redux for use in verifyVolumesThunk
    // Only include trailers where user made an actual change:
    // - reordered (originalPriority !== priority)
    // - volume was entered for a previously zero-volume trailer (volumeOverrideAllowed AND volume > 0)
    const trailerOverrideData: TrailerOverrideData[] = trailers
      .filter(t => (t.originalPriority && t.originalPriority !== t.priority) || (t.volumeOverrideAllowed && t.volume > 0))
      .map(t => ({
        trailerId: t.trailerId,
        priority: t.priority,
        originalPriority: t.originalPriority,
        volume: t.volume,
        reorderReason: t.reorderReason,
        reorderComment: t.reorderComment,
        volumeOverrideReason: t.volumeOverrideReason,
        volumeOverrideComment: t.volumeOverrideComment,
      }));
    dispatch(setTrailerOverrides(trailerOverrideData));

    try {
      await verifyTrailers(trailers);
      dispatch(showNotification({
        title: "Trailers Verified",
        message: "Trailers verified successfully!",
        type: "success"
      }));
      navigate(ROUTES.SHIFT_PLAN_VOLUMES);
    } catch (error) {
      console.error("Error verifying trailers:", error);
    }
  };

  return (
    <Box spacingInset="300" className="inbound-trailer-volume">
      {/* Title */}
      <Box spacingInset="200 none">
        <Heading type="h400" level={2}>
          Inbound Trailer Volume
        </Heading>
        <Text>Verify or re-order the trailers below and save.</Text>
      </Box>

      {/* SVG illustration — floats right, overlapping title area and search bar */}
      <img
        src={inboundTrailersImage}
        alt="Inbound Trailers"
        className="trailer-illustration-img"
      />

      {/* Same actions as the footer, so operators don't have to scroll the
          trailer table to continue. */}
      {!readOnly && (
        <Footer
          position="top"
          onClearOverrides={handleClearOverrides}
          onNext={handleVerify}
        />
      )}

      {/* Search + Expand All bar in grey panel */}
      <Box className="trailer-search-panel" spacingInset="300">
        <Row alignmentVertical="center" widths={["fit", "fill"]} spacing="400">
          <SearchField
            value={query}
            onChange={(value) => {
              setQuery(value);
              handleSearch(value);
            }}
            placeholder="Search for..."
            onSubmit={handleSearch}
            width={260}
          />
          <Row alignmentHorizontal="end" alignmentVertical="center" spacing="200">
            <Toggle checked={expandAll} onChange={() => setExpandAll(!expandAll)} />
            <Text>Expand All</Text>
          </Row>
        </Row>
      </Box>

      <DataTable
        showRowDividers
        showBorder
        width="100%"
        size="large"
      >
        <DataTableHead>
          <DataTableRow>
            <DataTableHeader>Priority</DataTableHeader>
            <DataTableHeader>Trailer Score</DataTableHeader>
            <DataTableHeader>Trailer ID</DataTableHeader>
            <DataTableHeader>Carrier</DataTableHeader>
            <DataTableHeader>Location</DataTableHeader>
            <DataTableHeader>Volume</DataTableHeader>
            <DataTableHeader></DataTableHeader>
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {loading ? (
            <DataTableRow>
              <DataTableCell colSpan={7}>
                <Text color="secondary">Loading trailers...</Text>
              </DataTableCell>
            </DataTableRow>
          ) : trailers.length === 0 ? (
            <DataTableRow>
              <DataTableCell colSpan={7}>
                <Text color="secondary">No trailers found.</Text>
              </DataTableCell>
            </DataTableRow>
          ) : (
            trailers.map((trailer, index) => {
              const isExpanded = expandedRows.has(trailer.trailerId);
              return (
                <React.Fragment key={trailer.trailerId}>
                  <DataTableRow
                    rowId={trailer.trailerId}
                    rowName={`Trailer ${trailer.trailerId}`}
                    highlightOnHover
                    onReorder={readOnly ? undefined : handleReorder}
                    isExpanded={isExpanded}
                  >
                    <DataTableCell
                      size="small"
                      hasExpander
                      expandLabel={`Expand trailer ${trailer.trailerId}`}
                      collapseLabel={`Collapse trailer ${trailer.trailerId}`}
                      onExpand={() => toggleRow(trailer.trailerId)}
                    >
                      <Text>{trailer.priority}</Text>
                    </DataTableCell>
                    <DataTableCell>{trailer.trailerScore.toLocaleString()}</DataTableCell>
                    <DataTableCell>{trailer.trailerId}</DataTableCell>
                    <DataTableCell>{trailer.carrier}</DataTableCell>
                    <DataTableCell>
                      <Row alignmentVertical="center" spacing="200">
                        <Text>{trailer.location}</Text>
                        {trailer.hasRedTag && <Tag type="error">Red Tag</Tag>}
                        {trailer.hasYellowTag && <Tag type="warning">Yellow Tag</Tag>}
                      </Row>
                    </DataTableCell>
                    <DataTableCell>
                      {trailer.inTransit && trailer.eta ? (
                        <Box>
                          <Text>In Transit</Text>
                          <Text type="b100" color="secondary">(ETA: {trailer.eta})</Text>
                        </Box>
                      ) : trailer.volumeOverrideAllowed ? (
                        readOnly ? (
                          <Text>{trailer.volume ? trailer.volume.toLocaleString() : '0'}</Text>
                        ) : (
                        <VolumeInput trailerId={trailer.trailerId} volume={trailer.volume} onUpdate={handleUpdateTrailer} />
                        )
                      ) : (
                        trailer.volume != null && trailer.volume !== 0
                          ? trailer.volume.toLocaleString()
                          : <Text color="secondary">0</Text>
                      )}
                    </DataTableCell>
                    <DataTableCell>
                      {!readOnly && <DataTableDragButton label={`Reorder trailer ${trailer.trailerId}`} />}
                    </DataTableCell>
                  </DataTableRow>
                  {isExpanded && (
                    <TrailerExpandedDetail
                      trailer={trailer}
                      totalTrailers={trailers.length}
                      onUpdateTrailer={handleUpdateTrailer}
                      onSetPriority={handleSetPriority}
                    />
                  )}
                </React.Fragment>
              );
            })
          )}
        </DataTableBody>
      </DataTable>

      {!readOnly && (
        <Footer
          onClearOverrides={handleClearOverrides}
          onNext={handleVerify}
        />
      )}
    </Box>
  );
};

export default InboundTrailerVolume;
import './index.scss';